import { useCallback, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTaskTypeInstructions } from '../../hooks/useTaskTypeInstructions';
import { saveTaskTypeInstructions } from '../../services/taskTypeSettings';
import { getTaskTypeDescriptor } from '../../taskTypes/descriptor';
import { useTaskTypeRuntime } from '../../taskTypes/useTaskTypeRuntime';
import { TaskTypeInstructions } from '../../types';
import { logGlobalError } from '../../utils/globalErrorLogger';

export function useAdminTaskTypeViewModel(taskTypeId: string | undefined) {
  const { currentUser } = useAuth();
  const [busy, setBusy] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const descriptor = useMemo(() => {
    if (!taskTypeId) return null;
    const candidate = getTaskTypeDescriptor(taskTypeId);
    return candidate.id === taskTypeId && candidate.active ? candidate : null;
  }, [taskTypeId]);
  const runtime = useTaskTypeRuntime(descriptor?.id ?? null);
  const instructionsState = useTaskTypeInstructions(descriptor?.id ?? null);

  const initialize = useCallback(() => {
    setBusy(true);
    setBusy(false);
  }, []);

  const save = useCallback(
    async (instructions: TaskTypeInstructions | null) => {
      if (!currentUser || !descriptor) {
        throw new Error('You must be signed in to edit task type instructions.');
      }
      setSaving(true);
      try {
        await saveTaskTypeInstructions(descriptor.id, currentUser, instructions);
      } catch (err) {
        logGlobalError(err, {
          context: 'AdminTaskTypePage.saveInstructions',
          metadata: { taskTypeId: descriptor.id, kind: instructions?.kind ?? null },
        });
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [currentUser, descriptor],
  );

  return { busy, saving, descriptor, runtime, instructionsState, initialize, save };
}
